import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Mail, Lock, ArrowRight, Download, Share, Eye, EyeOff, WifiOff, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    location.state?.error ? { type: "auth", message: location.state.error } : null
  );
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;

  const handleInstallPrompt = useCallback((e) => {
    e.preventDefault();
    setDeferredPrompt(e);
  }, []);

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
  }, [handleInstallPrompt]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.is_super_admin) {
        navigate("/super-admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        if (status >= 500) {
          setError({
            type: "server",
            message: "Il server ha riscontrato un problema temporaneo. Riprova tra qualche istante.",
          });
        } else if (status === 429) {
          setError({
            type: "auth",
            message: formatApiError(detail) || "Troppi tentativi. Attendi qualche minuto e riprova.",
          });
        } else {
          setError({
            type: "auth",
            message: formatApiError(detail) || "Email o password non corretti.",
          });
        }
      } else {
        setError({
          type: "network",
          message:
            navigator.onLine === false
              ? "Sembri offline. Controlla la connessione a Internet e riprova."
              : "Non riusciamo a contattare il server.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24 max-w-xl mx-auto lg:mx-0">
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight font-['Outfit']">Traslochi</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter font-['Outfit'] mb-2">
            Benvenuto
          </h1>
          <p className="text-zinc-500 text-sm">
            Accedi al gestionale traslochi
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && error.type === "network" && (
            <div
              data-testid="login-error"
              className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <div className="flex items-center gap-2 font-semibold">
                <WifiOff className="w-4 h-4 shrink-0" />
                Errore di connessione al server
              </div>
              <p className="mt-1 text-amber-800">{error.message} Prova questi passaggi:</p>
              <ul className="mt-2 space-y-1 text-amber-800 list-disc pl-5">
                <li>Ricarica la pagina forzando l'aggiornamento (<strong>Ctrl+Shift+R</strong> o <strong>Cmd+Shift+R</strong>).</li>
                <li>Verifica la connessione a Internet.</li>
                <li>Disattiva temporaneamente antivirus o estensioni che bloccano le richieste, oppure aggiungi questo sito alle eccezioni.</li>
                <li>Prova una finestra di navigazione in incognito.</li>
              </ul>
            </div>
          )}
          {error && error.type !== "network" && (
            <div
              data-testid="login-error"
              className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-4 py-3"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error.message}</span>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="pl-10 rounded-sm"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                id="password"
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                className="pl-10 pr-10 rounded-sm"
                required
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full bg-[#002FA7] hover:bg-[#002482] rounded-sm h-11 font-medium"
          >
            {loading ? "Accesso in corso..." : "Accedi"}
            {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-500">
          Non hai un account?{" "}
          <Link to="/register" className="text-[#002FA7] font-medium hover:underline" data-testid="go-to-register-link">
            Registra Azienda
          </Link>
        </p>

        {/* Install APP */}
        {!isStandalone && (
          <div className="mt-6">
            <button
              onClick={handleInstallClick}
              data-testid="install-app-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-sm border border-dashed border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7] hover:bg-blue-50/30 transition-colors text-sm font-medium"
            >
              {isIOS ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              Installa APP
            </button>
          </div>
        )}

        {/* iOS install guide */}
        {showIOSGuide && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowIOSGuide(false)}
            data-testid="ios-guide-overlay"
          >
            <div
              className="bg-white w-full sm:max-w-sm sm:rounded-sm rounded-t-2xl shadow-xl p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
              data-testid="ios-guide-modal"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-semibold font-['Outfit']">Installa Traslochi</h3>
              </div>
              <ol className="space-y-3 text-sm text-zinc-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <span>Tocca l'icona <Share className="w-4 h-4 inline text-[#002FA7] -mt-0.5" /> <strong>Condividi</strong> nella barra di Safari</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <span>Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <span>Tocca <strong>"Aggiungi"</strong> in alto a destra</span>
                </li>
              </ol>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="w-full bg-[#002FA7] text-white rounded-sm py-2.5 text-sm font-medium hover:bg-[#002482] transition-colors"
                data-testid="ios-guide-close"
              >
                Ho capito
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right: Hero image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7] to-[#001B61]">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/c19459f0-af49-45b4-872d-77ccc3b5a2a2/images/f3fe7c03a9aad4e33acc72908ce0382ea11f4ca2cd3f0fb8734f5536b09238f9.png"
            alt="Moving boxes illustration"
            className="w-full h-full object-cover opacity-40 mix-blend-luminosity"
          />
          <div className="absolute inset-0 flex items-end p-16">
            <div>
              <h2 className="text-white text-3xl font-bold font-['Outfit'] tracking-tight mb-3">
                Gestisci i tuoi traslochi
              </h2>
              <p className="text-white/70 text-sm max-w-md">
                Preventivi, inventari e fatture proforma in un unico sistema integrato.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
